import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import Input from '../../../components/Input';
import Switch from '../../../components/Switch';
import { getDescriptionError } from '../../../utils/validation';

/**
 * Add/Edit VNeID Account Modal Component
 *
 * @param {Boolean} open - Whether the modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {Function} onConfirm - Callback when account is confirmed with account data
 * @param {Object} accountData - Account data for editing (null for adding)
 * @param {Boolean} editMode - Whether in edit mode
 * @param {Boolean} loading - Whether the form is submitting
 */
function AddVNeIDAccountModal({ open, onClose, onConfirm, accountData = null, editMode = false, loading = false, units = [] }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    unitId: '',
    unitCodeId: '',
    accountName: '',
    password: '',
    description: '',
    status: true,
  });
  const [errors, setErrors] = useState({});

  // Populate form data when in edit mode
  useEffect(() => {
    if (editMode && accountData) {
      // Find unit by name to get its id (backend returns unitName, not unitId)
      let unitId = accountData.unitId || '';
      if (!unitId && accountData.unitName && units.length > 0) {
        const matchedUnit = units.find(u => u.name === accountData.unitName);
        if (matchedUnit) {
          unitId = matchedUnit.id;
        }
      }
      setFormData({
        unitId: unitId,
        unitCodeId: accountData.unitCodeId || '',
        accountName: accountData.accountName || '',
        password: '', // Password not populated in edit mode for security
        description: accountData.description || '',
        status: accountData.status !== undefined ? accountData.status : true,
      });
    } else {
      setFormData({
        unitId: '',
        unitCodeId: '',
        accountName: '',
        password: '',
        description: '',
        status: true,
      });
    }
    setErrors({});
  }, [editMode, accountData, open, units]);

  // Handle input change
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });

    // Validate field in real-time
    const newErrors = { ...errors };

    if (field === 'description') {
      const descriptionError = getDescriptionError(value, t);
      if (descriptionError) {
        newErrors.description = descriptionError;
      } else {
        delete newErrors.description;
      }
    }

    setErrors(newErrors);
  };

  // Handle switch change
  const handleSwitchChange = (event) => {
    setFormData({
      ...formData,
      status: event.target.checked,
    });
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    const descriptionError = getDescriptionError(formData.description, t);
    if (descriptionError) newErrors.description = descriptionError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle confirm
  const handleConfirm = () => {
    if (!validateForm()) {
      return;
    }

    // Convert unitId to unitName for backend
    const selectedUnit = units.find(u => u.id === formData.unitId);
    const unitName = selectedUnit ? selectedUnit.name : '';

    const dataToSend = {
      ...formData,
      unitName: unitName,
    };
    // Remove unitId as backend expects unitName
    delete dataToSend.unitId;

    if (editMode && accountData) {
      // Include the account ID for update
      onConfirm({ ...dataToSend, id: accountData.id });
    } else {
      onConfirm(dataToSend);
    }
    handleClose();
  };

  // Handle close and reset form
  const handleClose = () => {
    setFormData({
      unitId: '',
      unitCodeId: '',
      accountName: '',
      password: '',
      description: '',
      status: true,
    });
    onClose();
  };

  // Check if form is valid
  const isFormValid = () => {
    // Password is required when adding, optional when editing
    const passwordValid = editMode || formData.password.trim() !== '';
    return formData.unitId !== '' && formData.accountName.trim() !== '' && passwordValid;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      id="vneid-account-modal"
      data-testid="vneid-account-modal"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {editMode ? <EditIcon /> : <AddIcon />}
          {editMode ? t('vneid.accounts.modal.editTitle') : t('vneid.accounts.modal.addTitle')}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {/* Unit */}
          <FormControl fullWidth required>
            <InputLabel id="unit-label">{t('vneid.accounts.modal.unit')}</InputLabel>
            <Select
              labelId="unit-label"
              value={formData.unitId}
              onChange={(e) => setFormData({ ...formData, unitId: e.target.value })}
              label={t('vneid.accounts.modal.unit')}
              id="unitId"
              name="unitId"
              data-testid="vneid-account-modal-select-unitId"
            >
              {units.map((unit) => (
                <MenuItem key={unit.id} value={unit.id}>
                  {unit.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Unit Code ID */}
          <Input
            label={t('vneid.accounts.modal.unitCodeId')}
            value={formData.unitCodeId}
            onChange={handleChange('unitCodeId')}
            placeholder={t('vneid.accounts.modal.unitCodeIdPlaceholder')}
            fullWidth
            id="unitCodeId"
            name="unitCodeId"
            data-testid="vneid-account-modal-input-unitCodeId"
          />

          {/* Account Name */}
          <Input
            label={t('vneid.accounts.modal.accountName')}
            value={formData.accountName}
            onChange={handleChange('accountName')}
            placeholder={t('vneid.accounts.modal.accountNamePlaceholder')}
            required
            fullWidth
            disabled={editMode} // Can't change account name when editing
            id="accountName"
            name="accountName"
            data-testid="vneid-account-modal-input-accountName"
          />

          {/* Password */}
          <Input
            label={t('vneid.accounts.modal.password')}
            value={formData.password}
            onChange={handleChange('password')}
            placeholder={editMode ? t('vneid.accounts.modal.passwordPlaceholderEdit') : t('vneid.accounts.modal.passwordPlaceholder')}
            required={!editMode}
            fullWidth
            type="password"
            id="password"
            name="password"
            data-testid="vneid-account-modal-input-password"
          />

          {/* Description */}
          <Input
            label={t('vneid.accounts.modal.description')}
            value={formData.description}
            onChange={handleChange('description')}
            placeholder={t('vneid.accounts.modal.descriptionPlaceholder')}
            fullWidth
            multiline
            rows={3}
            error={!!errors.description}
            helperText={errors.description}
            id="description"
            name="description"
            data-testid="vneid-account-modal-input-description"
          />

          {/* Status */}
          <Switch
            label={t('vneid.accounts.modal.status')}
            checked={formData.status}
            onChange={handleSwitchChange}
            id="status"
            name="status"
            data-testid="vneid-account-modal-switch-status"
          />

          {/* API Key - Show in edit mode */}
          {editMode && accountData?.apiKey && (
            <Input
              label={t('vneid.accounts.modal.apiKey')}
              value={accountData.apiKey}
              fullWidth
              disabled
              id="apiKey"
              name="apiKey"
              data-testid="vneid-account-modal-input-apiKey"
            />
          )}

          {/* API Key Info (only for add mode) */}
          {!editMode && (
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>{t('vneid.accounts.modal.apiKey')}:</strong> {t('vneid.accounts.modal.apiKeyGenerated')}
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loading}
          id="cancel"
          name="cancel"
          data-testid="vneid-account-modal-button-cancel"
        >
          {t('vneid.accounts.modal.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!isFormValid() || loading}
          id="confirm"
          name="confirm"
          data-testid="vneid-account-modal-button-confirm"
        >
          {loading ? '...' : t('vneid.accounts.modal.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddVNeIDAccountModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  accountData: PropTypes.object,
  editMode: PropTypes.bool,
  loading: PropTypes.bool,
  units: PropTypes.array,
};

export default AddVNeIDAccountModal;
